import { useMemo } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { usePocketsVisibility } from "@/modules/dashboard/cards/hooks/usePocketsVisibility";
import { useOtcAccess } from "@/modules/dashboard/crypto-swaps/hooks/useOtcAccess";
import { useInternalApiKeyAccess } from "@/modules/dashboard/internal-api-keys/hooks/useInternalApiKeyAccess";
import { useMetricsAccess } from "@/modules/dashboard/metrics/hooks/useMetricsAccess";
import { useWalletTopUpAccess } from "@/modules/dashboard/wallet-top-ups/hooks/useWalletTopUpAccess";

import type { NavContext } from "./navigation.registry";
import { buildNavContext } from "./resolveNavAccess";

/**
 * The resolver's input, memoized per session.
 *
 * `user.scopes` is authoritative since the backend started projecting the RBAC-effective permissions onto it,
 * so there is no service, slice or thunk behind this — just a `Set` built once instead of an `Array.includes`
 * per entry per render.
 *
 * `usePocketsVisibility` is safe to call from several places: it caches per organization at module level and
 * de-duplicates in-flight requests, so the sidebar and the route guards share one call.
 */
export function useNavContext(): NavContext | null {
  const { user } = useAuth();
  const { visible: pocketsVisible } = usePocketsVisibility();
  const { allowed: walletTopUpsAllowed } = useWalletTopUpAccess();
  const { enabled: cryptoEnabled } = useOtcAccess();
  const { allowed: metricsAllowed } = useMetricsAccess();
  const { allowed: internalApiKeysAllowed } = useInternalApiKeyAccess();

  return useMemo(
    () =>
      buildNavContext(user, {
        pocketsVisible,
        walletTopUpsAllowed,
        metricsAllowed,
        internalApiKeysAllowed,
        cryptoEnabled,
      }),
    // `user.scopes` is replaced wholesale by the auth reducer, so the identity check is enough.
    [user, pocketsVisible, walletTopUpsAllowed, metricsAllowed, internalApiKeysAllowed, cryptoEnabled],
  );
}
